/**
 * Consumer Portal Navigation Configuration
 *
 * Defines persona-based navigation for the Consumer Portal.
 * Each persona (consumer type) has its own navigation configuration.
 *
 * @see Issue #330 - Unified Navigation System
 */

import type { SidebarConfig } from '../types';

/**
 * Consumer type identifiers for the Consumer Portal.
 * Partners (CHANNEL_PARTNER, REFERRAL_PARTNER) use the Business Portal instead.
 */
export type ConsumerType = 'LOAN_APPLICANT' | 'END_CONSUMER';

/**
 * Consumer Portal Navigation Configs by Persona
 *
 * Each persona has a tailored navigation experience:
 * - LOAN_APPLICANT: Borrower applying for loans
 * - END_CONSUMER: General consumer submitting cases
 *
 * Note: Partners (CHANNEL_PARTNER, REFERRAL_PARTNER) use the Business Portal.
 */
export const CONSUMER_PORTAL_NAV_CONFIGS: Record<ConsumerType, SidebarConfig> = {
  /**
   * Loan Applicant (Borrower) Navigation — Astor-first IA per Fiops Borrower Portal redesign.
   *
   * Layout (top to bottom):
   *   Astor      (leads with the AI surface, distinguishing it from the rest of nav)
   *   Home       (briefing landing)
   *   ─ Work ─
   *     Loans       (renamed from "Applications" — borrowers think "loans", not "applications")
   *     Offers      (renamed from "Proposals")
   *     Sign queue  (renamed from "Documents to sign")
   *   ─ Reference ─
   *     Documents
   *     Entities
   *     Guarantees
   *     Watchlist
   *
   * The legacy `/chat` route is folded into the toolbar sparkle (Astor side-panel)
   * + the top-of-nav Astor item. No dedicated chat nav row.
   */
  LOAN_APPLICANT: {
    portalId: 'consumer',
    portalName: 'Borrower Portal',
    width: 260,
    storageKey: 'consumer-sidebar-state',
    layoutMode: 'rail',

    standaloneItems: [
      {
        id: 'astor',
        label: 'navigation.astor',
        path: '/chat',
        icon: 'AutoAwesome',
        keywords: ['ai', 'assistant', 'astor', 'help', 'chat'],
      },
      {
        id: 'home',
        label: 'navigation.home',
        path: '/dashboard',
        icon: 'Home',
        keywords: ['dashboard', 'overview', 'home', 'briefing'],
      },
    ],

    sections: [
      {
        id: 'work',
        label: 'navigation.work',
        icon: 'Assignment',
        items: [
          {
            id: 'applications',
            label: 'navigation.loans',
            path: '/applications',
            icon: 'Assignment',
            keywords: ['loans', 'requests', 'applications'],
          },
          {
            id: 'proposals',
            label: 'navigation.offers',
            path: '/proposals',
            icon: 'Handshake',
            keywords: ['offers', 'quotes', 'proposals', 'term sheets'],
          },
          // Issue #1308 — Sign queue. Badge count is populated dynamically by
          // PageTemplate via badgeCounts prop. Feature-flag gated.
          {
            id: 'documents-to-sign',
            label: 'navigation.signQueue',
            path: '/documents-to-sign',
            icon: 'HistoryEdu',
            keywords: ['sign', 'e-signature', 'esign', 'docuseal', 'pending', 'signature', 'queue'],
            visibility: { requireFeatureFlag: 'enable_termsheet_signing_v2' },
            badgePollInterval: 60_000,
          },
        ],
      },
      {
        id: 'reference',
        label: 'navigation.reference',
        icon: 'Folder',
        items: [
          {
            id: 'documents',
            label: 'navigation.documents',
            path: '/documents',
            icon: 'Folder',
            keywords: ['documents', 'files', 'uploads', 'vault'],
          },
          {
            id: 'entities',
            label: 'navigation.entities',
            path: '/entities',
            icon: 'AccountBalance',
            keywords: ['entities', 'borrowing', 'llc', 'companies'],
          },
          {
            id: 'guarantors',
            label: 'navigation.guarantees',
            path: '/guarantors',
            icon: 'Shield',
            keywords: ['guarantor', 'guarantee', 'exposure', 'obligations'],
          },
          {
            id: 'watchlist',
            label: 'navigation.watchlist',
            path: '/watchlist',
            icon: 'Bookmark',
            keywords: ['watchlist', 'saved', 'pinned', 'bookmarks', 'favorites', 'starred'],
          },
        ],
      },
    ],
  },

  /**
   * End Consumer Navigation — mirrors the LOAN_APPLICANT IA so both
   * personas in the borrower portal share the same Astor-first chrome.
   */
  END_CONSUMER: {
    portalId: 'consumer',
    portalName: 'Consumer Portal',
    width: 260,
    storageKey: 'consumer-sidebar-state',
    layoutMode: 'rail',

    standaloneItems: [
      {
        id: 'astor',
        label: 'navigation.astor',
        path: '/chat',
        icon: 'AutoAwesome',
        keywords: ['ai', 'assistant', 'astor', 'help', 'chat'],
      },
      {
        id: 'home',
        label: 'navigation.home',
        path: '/dashboard',
        icon: 'Home',
        keywords: ['dashboard', 'overview', 'home', 'briefing'],
      },
    ],

    sections: [
      {
        id: 'work',
        label: 'navigation.work',
        icon: 'Assignment',
        items: [
          {
            id: 'applications',
            label: 'navigation.loans',
            path: '/applications',
            icon: 'FolderShared',
            keywords: ['loans', 'requests', 'applications', 'cases'],
          },
          {
            id: 'proposals',
            label: 'navigation.offers',
            path: '/proposals',
            icon: 'Handshake',
            keywords: ['offers', 'quotes', 'proposals', 'term sheets'],
          },
          {
            id: 'documents-to-sign',
            label: 'navigation.signQueue',
            path: '/documents-to-sign',
            icon: 'HistoryEdu',
            keywords: ['sign', 'e-signature', 'esign', 'docuseal', 'pending', 'signature', 'queue'],
            visibility: { requireFeatureFlag: 'enable_termsheet_signing_v2' },
            badgePollInterval: 60_000,
          },
        ],
      },
      {
        id: 'reference',
        label: 'navigation.reference',
        icon: 'Folder',
        items: [
          {
            id: 'documents',
            label: 'navigation.documents',
            path: '/documents',
            icon: 'Folder',
            keywords: ['documents', 'files', 'uploads', 'vault'],
          },
          {
            id: 'entities',
            label: 'navigation.entities',
            path: '/entities',
            icon: 'AccountBalance',
            keywords: ['entities', 'borrowing', 'llc', 'companies'],
          },
          {
            id: 'guarantors',
            label: 'navigation.guarantees',
            path: '/guarantors',
            icon: 'Shield',
            keywords: ['guarantor', 'guarantee', 'exposure', 'obligations'],
          },
          {
            id: 'watchlist',
            label: 'navigation.watchlist',
            path: '/watchlist',
            icon: 'Bookmark',
            keywords: ['watchlist', 'saved', 'pinned', 'bookmarks', 'favorites', 'starred'],
          },
        ],
      },
    ],
  },
};

/**
 * Get the navigation config for a consumer type, with fallback to END_CONSUMER.
 * Partners (CHANNEL_PARTNER, REFERRAL_PARTNER) should use the Business Portal;
 * if they somehow reach the Consumer Portal, they get the END_CONSUMER nav.
 */
export function getConsumerNavConfig(consumerType: string): SidebarConfig {
  return CONSUMER_PORTAL_NAV_CONFIGS[consumerType as ConsumerType] ?? CONSUMER_PORTAL_NAV_CONFIGS.END_CONSUMER;
}
